import { useState } from 'react';
import { Link } from 'react-router-dom';
import { imageUrl } from '../lib/images';
import Lightbox from './Lightbox';

export default function BrandingSection({ className = '', compact = false }) {
  const [isPosterLightboxOpen, setIsPosterLightboxOpen] = useState(false);

  const branches = [
    { title: 'Caféer & Restauranter', icon: 'fa-solid fa-mug-saucer' },
    { title: 'Butikker & Webshops', icon: 'fa-solid fa-shirt' },
    { title: 'Fitness & Sundhed', icon: 'fa-solid fa-dumbbell' },
    { title: 'Klinikker & Behandlere', icon: 'fa-solid fa-hand-holding-heart' },
    { title: 'Håndværkere & Service', icon: 'fa-solid fa-screwdriver-wrench' },
    { title: 'Virksomheder & Kontorer', icon: 'fa-solid fa-building' },
  ];

  const packages = [
    {
      badge: 'PAKKE 1',
      title: 'FOTO + VIDEO SHOOT',
      subtitle: 'En komplet visuel fortælling om jeres virksomhed.',
      price: '7.500 kr.',
      period: 'ekskl. moms',
      popular: false,
      features: [
        '20 professionelt redigerede billeder',
        '1 video på ca. 30 sekunder',
        'Indhold målrettet jeres brand og målgruppe',
        'Perfekt til hjemmeside, sociale medier og markedsføring',
      ],
      contactParam: 'Pakke 1: Foto + Video Shoot',
    },
    {
      badge: 'PAKKE 2',
      title: 'CONTENT ABONNEMENT',
      subtitle: 'Løbende indhold, der holder jeres sociale medier levende.',
      price: '3.500 kr.',
      period: 'ekskl. moms / måned',
      popular: true,
      features: [
        'Månedligt besøg',
        '20 professionelt redigerede billeder hver måned',
        'Indhold tilpasset sæsoner, events og jeres behov',
        'Perfekt til sociale medier og hjemmeside',
      ],
      contactParam: 'Pakke 2: Content Abonnement',
    },
    {
      badge: 'PAKKE 3',
      title: 'CONTENT PLUS',
      subtitle: 'Den komplette løsning til jer, der ønsker endnu mere synlighed.',
      price: '4.500 kr.',
      period: 'ekskl. moms / måned',
      popular: false,
      features: [
        'Månedligt besøg',
        '30 professionelt redigerede billeder hver måned',
        '1 reel/video på op til 15 sekunder hver måned',
        'Indhold tilpasset sæsoner, events og jeres behov',
        'Perfekt til sociale medier, hjemmeside og kampagner',
      ],
      contactParam: 'Pakke 3: Content Plus',
    },
  ];

  const values = [
    { label: 'Autentisk indhold', icon: 'fa-solid fa-camera' },
    { label: 'Styrker jeres brand', icon: 'fa-solid fa-heart' },
    { label: 'Flere kunder & bookinger', icon: 'fa-solid fa-chart-line' },
    { label: 'Sparer jer tid', icon: 'fa-solid fa-clock' },
    { label: 'Tilpasset jeres branche', icon: 'fa-solid fa-users' },
    { label: 'Fleksible løsninger', icon: 'fa-solid fa-calendar-check' },
  ];

  if (compact) {
    return (
      <section
        id="branding"
        className={`py-14 md:py-20 bg-white text-[#141416] border-y border-stone-200 ${className}`}
        aria-label="Branding og markedsføring til caféer, restauranter og erhverv"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-5">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-medium text-stone-900 tracking-tight leading-tight">
                Styrk jeres synlighed med professionelt foto og video
              </h2>

              <p className="text-base sm:text-lg text-[#8C6B3D] font-serif italic">
                Billeder skal ikke bare være flotte. De skal føles rigtige.
              </p>

              <p className="text-stone-700 text-sm sm:text-base leading-relaxed font-sans">
                Når du booker Lunde Media, begynder opgaven ikke med kameraet – den begynder med dig og jeres fortælling. 
                Skræddersyet foto- og videoproduktion til caféer, restauranter, butikker, klinikker og virksomheder, der vil ses af de rette kunder.
              </p>

              {/* Compact highlights */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
                <div className="p-3.5 bg-white border border-stone-200 shadow-sm text-center">
                  <div className="text-stone-900 font-serif text-lg font-semibold">Shoot</div>
                  <div className="text-[11px] text-stone-500 mt-0.5">Foto + Video pakke</div>
                </div>
                <div className="p-3.5 bg-white border-2 border-[#A67C46]/50 shadow-sm text-center">
                  <div className="text-[#8C6B3D] font-serif text-lg font-semibold">Abonnement</div>
                  <div className="text-[11px] text-stone-600 font-medium mt-0.5">Fast månedligt content</div>
                </div>
                <div className="p-3.5 bg-white border border-stone-200 shadow-sm text-center">
                  <div className="text-stone-900 font-serif text-lg font-semibold">Plus</div>
                  <div className="text-[11px] text-stone-500 mt-0.5">Maksimal synlighed</div>
                </div>
              </div>

              {/* CTAs */}
              <div className="pt-2 flex flex-wrap items-center gap-3">
                <Link
                  to="/branding"
                  className="cta-btn inline-flex items-center gap-2 px-7 py-3.5 bg-[#E87A38] hover:bg-[#d66e2f] text-white font-medium text-xs uppercase tracking-wider transition-all shadow-sm"
                >
                  <span>Læs mere & se pakker</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>

                <Link
                  to="/branding#kontakt-sektion"
                  className="inline-flex items-center gap-2 px-5 py-3.5 border border-stone-300 hover:border-stone-900 text-stone-800 font-medium text-xs uppercase tracking-wider transition-all bg-white shadow-sm"
                >
                  <i className="fa-solid fa-comments text-[#E87A38]" aria-hidden="true" />
                  <span>Book et shoot</span>
                </Link>
              </div>
            </div>

            {/* Right Visual Teaser */}
            <div className="lg:col-span-5">
              <div className="relative group rounded-none overflow-hidden border border-stone-200 bg-white p-2 shadow-md max-w-md mx-auto">
                <img
                  src={imageUrl('/_assets/bookninger.png', 'width=700,quality=90,format=auto')}
                  alt="Lunde Media - Branding & Markedsføring for caféer og erhverv"
                  className="w-full h-auto object-cover cursor-pointer transition-transform duration-500 group-hover:scale-[1.02]"
                  onClick={() => setIsPosterLightboxOpen(true)}
                />
                <div
                  onClick={() => setIsPosterLightboxOpen(true)}
                  className="absolute inset-0 bg-stone-900/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer p-4"
                >
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-white text-stone-900 font-medium text-[11px] uppercase tracking-wider shadow">
                    <i className="fa-solid fa-magnifying-glass-plus" aria-hidden="true" />
                    <span>Se oversigten</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Lightbox for zooming bookninger.png */}
        <Lightbox
          images={['/_assets/bookninger.png']}
          currentIndex={0}
          isOpen={isPosterLightboxOpen}
          onClose={() => setIsPosterLightboxOpen(false)}
          onNavigate={() => {}}
        />
      </section>
    );
  }

  return (
    <section
      id="branding"
      className={`pt-6 pb-20 md:pt-8 md:pb-28 bg-[#FAF8F5] text-[#141416] border-y border-stone-200 ${className}`}
      aria-label="Branding og markedsføring til caféer, restauranter og erhverv"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8 space-y-12 md:space-y-16">
        {/* Hvem jeg arbejder med - Target Sectors (sat lige under hero) */}
        <div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {branches.map((b, i) => (
              <div
                key={i}
                className="p-4 bg-white border border-stone-200 text-center space-y-2 hover:border-[#A67C46]/50 transition-colors shadow-sm"
              >
                <i className={`${b.icon} text-lg text-[#A67C46]`} aria-hidden="true" />
                <div className="text-xs font-medium text-stone-800">{b.title}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Intro Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left Text */}
          <div className="lg:col-span-7 space-y-6">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-medium text-stone-900 tracking-tight leading-tight">
              Styrk jeres synlighed med professionelt foto og video
            </h2>

            <p className="text-lg sm:text-xl text-[#A67C46] font-serif italic">
              Billeder skal ikke bare være flotte. De skal føles rigtige.
            </p>

            <div className="space-y-4 text-stone-700 text-sm sm:text-base leading-relaxed font-sans">
              <p>
                Når du booker Lunde Media, begynder opgaven ikke med kameraet. Den begynder med dig.
                Jeg vil gerne forstå, hvem I er, hvad I laver, og vigtigst af alt: Hvad skal mennesker føle, når de ser jeres billeder eller jeres video?
              </p>
              <p>
                En café skal ikke fortælle den samme historie som en håndværker. En restaurant skal ikke ligne alle sine konkurrenter. Derfor bruger jeg tid på at forstå jeres hverdag, jeres stemning og de små detaljer, der gør jer til noget særligt.
              </p>
              <p className="text-stone-900 font-medium">
                Mindre opstillet. Mere autentisk. I behøver ikke være vant til et kamera – jeg skaber en rolig, afslappet og uformel stemning under shootet.
              </p>
            </div>

            {/* What you can expect bullet points */}
            <div className="pt-4 border-t border-stone-200 space-y-2.5 text-xs sm:text-sm text-stone-700">
              <div className="flex items-start gap-2.5">
                <i className="fa-solid fa-check text-[#A67C46] mt-1 shrink-0" aria-hidden="true" />
                <span>Personlig dialog før fotograferingen og klart fælles mål</span>
              </div>
              <div className="flex items-start gap-2.5">
                <i className="fa-solid fa-check text-[#A67C46] mt-1 shrink-0" aria-hidden="true" />
                <span>Rolig, tryg og uformel stemning under hele shootet</span>
              </div>
              <div className="flex items-start gap-2.5">
                <i className="fa-solid fa-check text-[#A67C46] mt-1 shrink-0" aria-hidden="true" />
                <span>Billeder og video skabt præcis til det sted, de skal bruges (hjemmeside, SoMe, annoncer)</span>
              </div>
            </div>

            {/* CTAs & PDF Download button */}
            <div className="pt-4 flex flex-wrap items-center gap-4">
              <Link
                to="/kontakt?emne=branding"
                className="cta-btn inline-flex items-center gap-2 px-8 py-3.5 bg-[#141416] hover:bg-black text-white font-semibold text-sm tracking-wide transition-all shadow-md"
              >
                <span>Book et branding-shoot</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>

              {/* PDF Download Button */}
              <a
                href="/_assets/bryllupsshoot-3.pdf"
                download
                className="inline-flex items-center gap-2 px-6 py-3.5 border border-stone-300 hover:border-[#A67C46] hover:text-[#8C6B3D] text-stone-800 font-medium text-sm tracking-wide transition-all bg-white shadow-sm"
                title="Download informationsmateriale som PDF"
              >
                <i className="fa-solid fa-file-pdf text-[#A67C46]" aria-hidden="true" />
                <span>Download materiale (PDF)</span>
              </a>
            </div>
          </div>

          {/* Right Visual Card showing bookninger.png */}
          <div className="lg:col-span-5">
            <div className="relative group rounded-none overflow-hidden border border-stone-200 bg-white shadow-xl">
              <img
                src={imageUrl('/_assets/bookninger.png', 'width=900,quality=90,format=auto')}
                alt="Lunde Media - Branding & Markedsføring for caféer, restauranter og forretninger"
                className="w-full h-auto object-cover cursor-pointer transition-transform duration-700 group-hover:scale-[1.02]"
                onClick={() => setIsPosterLightboxOpen(true)}
              />
              <div
                onClick={() => setIsPosterLightboxOpen(true)}
                className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer p-4"
              >
                <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/95 text-stone-900 font-medium text-xs uppercase tracking-wider shadow-lg">
                  <i className="fa-solid fa-magnifying-glass-plus" aria-hidden="true" />
                  <span>Se oversigten i fuld størrelse</span>
                </span>
              </div>
            </div>
            <p className="text-center text-xs text-stone-500 mt-3 font-sans">
              Klik på oversigten for at se pakker, eksempler og detaljer i fuld opløsning.
            </p>
          </div>
        </div>

        {/* The 3 Packages Grid */}
        <div className="space-y-8">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <h3 className="text-3xl sm:text-4xl font-serif text-stone-900">
              Vælg den rette pakke til jeres synlighed
            </h3>
            <p className="text-stone-600 text-sm leading-relaxed">
              Uanset om I skal bruge et engangsshoot eller et fast månedligt abonnement med løbende friskt content.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {packages.map((pkg, idx) => (
              <div
                key={idx}
                className={`relative flex flex-col justify-between p-8 border transition-all duration-300 ${
                  pkg.popular
                    ? 'bg-white border-[#B89B72] shadow-xl scale-[1.02] ring-1 ring-[#B89B72]'
                    : 'bg-white border-stone-200 hover:border-stone-400 shadow-sm'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#B89B72] text-stone-950 font-semibold text-xs uppercase tracking-wider shadow">
                    Mest populære valg
                  </div>
                )}

                <div className="space-y-5">
                  <div className="text-xs font-semibold uppercase tracking-wider text-[#8C6B3D]">
                    {pkg.badge}
                  </div>
                  <h4 className="text-2xl font-serif text-stone-900">{pkg.title}</h4>
                  <p className="text-stone-600 text-xs sm:text-sm leading-relaxed min-h-[40px]">
                    {pkg.subtitle}
                  </p>

                  <div className="pt-2 pb-4 border-y border-stone-200">
                    <div className="text-3xl font-serif font-bold text-stone-900">{pkg.price}</div>
                    <div className="text-xs text-stone-500 mt-0.5">{pkg.period}</div>
                  </div>

                  <ul className="space-y-3 text-xs sm:text-sm text-stone-700">
                    {pkg.features.map((feat, fIdx) => (
                      <li key={fIdx} className="flex items-start gap-2.5">
                        <i className="fa-solid fa-check text-[#A67C46] mt-0.5 shrink-0 text-xs" aria-hidden="true" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-8">
                  <Link
                    to={`/kontakt?emne=${encodeURIComponent(pkg.contactParam)}`}
                    className={`w-full inline-flex items-center justify-center px-6 py-3.5 text-xs uppercase tracking-wider font-semibold transition-all ${
                      pkg.popular
                        ? 'bg-[#B89B72] hover:bg-[#a38356] text-white shadow-md'
                        : 'bg-[#141416] hover:bg-black text-white shadow-sm'
                    }`}
                  >
                    <span>Vælg {pkg.badge}</span>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Values Banner */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 pt-10 border-t border-stone-200 text-center">
          {values.map((v, i) => (
            <div key={i} className="space-y-1.5 p-3">
              <i className={`${v.icon} text-sm text-[#A67C46]`} aria-hidden="true" />
              <div className="text-xs uppercase tracking-wider font-semibold text-stone-800">
                {v.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox for zooming bookninger.png */}
      <Lightbox
        images={['/_assets/bookninger.png']}
        currentIndex={0}
        isOpen={isPosterLightboxOpen}
        onClose={() => setIsPosterLightboxOpen(false)}
        onNavigate={() => {}}
      />
    </section>
  );
}
